import { Link } from '@tanstack/react-router'
import { ClipboardList } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useRegistrations } from '@/features/documents/hooks'
import {
  REGISTRATION_STATUS_LABELS,
  REGISTRATION_STATUS_VARIANT,
} from '@/types/documents'

const daysUntil = (iso?: string | null) => {
  if (!iso) return null
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/**
 * RegistrationsTab is the office's post-sale paperwork work queue: every
 * deal-won registration across the pipeline, with its temp-tag deadline
 * front and center. Rows link to the deal detail page where the checklist
 * lives.
 */
export function RegistrationsTab() {
  const { data: registrations = [], isLoading } = useRegistrations()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ClipboardList className="h-4 w-4" /> Registrations
        </CardTitle>
        <CardDescription>
          Title &amp; registration filings for every sold boat. Temporary tags are highlighted as
          their expiry approaches.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : registrations.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No registrations yet. Win a deal with a boat and its registration checklist starts
            automatically.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Boat</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Temp tag expires</TableHead>
                <TableHead>Started</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {registrations.map((reg) => {
                const days = daysUntil(reg.temp_tag_expires_at)
                const urgent =
                  days !== null && days <= 14 && reg.status !== 'completed'
                return (
                  <TableRow key={reg.id}>
                    <TableCell>
                      <Link
                        to="/sales/deals/$dealId"
                        params={{ dealId: reg.deal_id }}
                        className="font-medium hover:underline"
                      >
                        {reg.boat ? `${reg.boat.year} ${reg.boat.make} ${reg.boat.model}` : reg.deal_title}
                      </Link>
                      <div className="text-xs text-muted-foreground">{reg.deal_title}</div>
                    </TableCell>
                    <TableCell>
                      {reg.customer ? (
                        <Link
                          to="/sales/$customerId"
                          params={{ customerId: reg.customer.id }}
                          className="hover:underline"
                        >
                          {reg.customer.first_name} {reg.customer.last_name}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{reg.state || '—'}</TableCell>
                    <TableCell>
                      <Badge variant={REGISTRATION_STATUS_VARIANT[reg.status]}>
                        {REGISTRATION_STATUS_LABELS[reg.status]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {reg.temp_tag_expires_at ? (
                        <span className={urgent ? 'font-medium text-destructive' : ''}>
                          {formatDate(reg.temp_tag_expires_at)}
                          {urgent && days !== null && (
                            <span className="ml-1.5">
                              {days < 0 ? '· expired' : `· ${days}d left`}
                            </span>
                          )}
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(reg.created_at)}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}
